<script setup lang="ts">
import { computed, ref } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/dropdown-menu'
import MultiSelectDropdown from '@/components/MultiSelectDropdown.vue'
import { announce } from '@/util/live_announcer'
import AdminConfirmDialog from './AdminConfirmDialog.vue'

export interface SubjectAlignment {
  subject: string
  domains: string[]
}

const CONFIRM_GROUP = 'remove-subject'

const props = withDefaults(defineProps<{
  subjectAlignments: SubjectAlignment[]
  subjects?: string[]
  subjectDomainsMap?: Record<string, string[]>
}>(), {
  subjects: () => [],
  subjectDomainsMap: () => ({})
})

const emit = defineEmits<{ 'update:subjectAlignments': [alignments: SubjectAlignment[]] }>()

const confirm = useConfirm()
const addSubjectTrigger = ref<InstanceType<typeof DropdownMenuTrigger> | null>(null)

const unpickedSubjects = computed(() => {
  const picked = props.subjectAlignments.map(alignment => alignment.subject)
  return props.subjects.filter(subject => !picked.includes(subject))
})

const domainOptions = (subject: string) =>
  (props.subjectDomainsMap[subject] ?? []).map(domain => ({ label: domain, value: domain }))

function addSubject(subject: string) {
  emit('update:subjectAlignments', [...props.subjectAlignments, { subject, domains: [] }])
  announce(`Added subject ${subject}`)
}

function updateDomains(index: number, domains: string[]) {
  emit('update:subjectAlignments', props.subjectAlignments.map((alignment, i) =>
    i === index ? { ...alignment, domains } : alignment
  ))
}

function confirmRemoveSubject(index: number) {
  const { subject } = props.subjectAlignments[index]
  confirm.require({
    group: CONFIRM_GROUP,
    header: 'Remove subject',
    message: `Are you sure you want to remove ${subject} and its domains?`,
    accept: () => {
      emit('update:subjectAlignments', props.subjectAlignments.filter(alignment => alignment.subject !== subject))
      announce(`Removed subject ${subject}`)
      addSubjectTrigger.value?.$el?.focus()
    }
  })
}
</script>

<template>
  <div class="mb-4">
    <!-- reka owns the trigger id, so the heading is text and the button names itself -->
    <p class="mb-1 font-bold">Add Subject</p>
    <!-- non-modal: a modal menu marks the rest of the page aria-hidden while open (axe aria-hidden-focus) -->
    <DropdownMenu :modal="false">
      <DropdownMenuTrigger
        ref="addSubjectTrigger"
        data-testid="add-subject"
        class="inline-flex items-center gap-1 h-10 px-3 text-md text-gray-900 bg-white border border-gray-600 rounded-xs hover:bg-gray-300 focus-visible:outline-primary-400"
      >
        Add new subject
        <span class="material-icons-round text-display-sm" aria-hidden="true">arrow_drop_down</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="max-h-[var(--reka-popper-available-height)] overflow-y-auto bg-white">
        <DropdownMenuItem
          v-for="subject in unpickedSubjects"
          :key="subject"
          class="cursor-pointer hover:bg-gray-300"
          @select="addSubject(subject)"
        >
          {{ subject }}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <div
      v-for="(alignment, index) in subjectAlignments"
      :key="alignment.subject"
      class="mt-2 p-3 border border-gray-400 rounded-xs"
    >
      <div class="flex items-center justify-between mb-1">
        <label :for="`domains-select-${index}`" class="font-bold">{{ alignment.subject }}</label>
        <button
          type="button"
          class="inline-flex text-red-400 hover:text-red-300 focus-visible:outline-primary-400"
          :aria-label="`Remove subject ${alignment.subject}`"
          @click="confirmRemoveSubject(index)"
        >
          <span class="material-icons-round text-text-lg" aria-hidden="true">close</span>
        </button>
      </div>
      <MultiSelectDropdown
        :id="`domains-select-${index}`"
        :options="domainOptions(alignment.subject)"
        :model-value="alignment.domains"
        placeholder="Select domains"
        @update:model-value="domains => updateDomains(index, domains)"
      />
    </div>
    <AdminConfirmDialog :group="CONFIRM_GROUP" />
  </div>
</template>
